import { Components, Theme, ThemeOptions, SxProps } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────
// CARD LAYOUT STYLES
// ─────────────────────────────────────────────────────────

// Styles for card with left icon position (split layout)
export const cardLeftLayoutStyles: SxProps<Theme> = {
  display: 'flex',
  padding: 0,
  flexDirection: 'row',
  gap: (theme) => theme.unit(2),
};

// Styles for icon container in left position
export const cardLeftIconContainerStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'start',
  flexShrink: 0,
};

// Styles for header in left layout (removes default padding)
export const cardLeftHeaderStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  gap: (theme) => theme.unit(1.5),
  padding: 0,
};

// Styles for right section container in left layout
export const cardLeftRightSectionStyles: SxProps<Theme> = {
  flexGrow: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: (theme) => theme.unit(0.5),
};

const getCardTheme = (theme: Theme): ThemeOptions => {
  // ─────────────────────────────────────────────────────────
  // MUI CARD THEME
  // ─────────────────────────────────────────────────────────
  const cardTheme: Components<Theme>['MuiCard'] = {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: theme.unit(3),
        backgroundColor: theme.palette.M3.sys.light.surface,
        border: `1px solid ${theme.palette.M3.sys.light['outline 3']}`,
        overflow: 'hidden',
      },
    },
  };

  // ─────────────────────────────────────────────────────────
  // MUI CARD HEADER THEME
  // ─────────────────────────────────────────────────────────
  const cardHeaderTheme: Components<Theme>['MuiCardHeader'] = {
    styleOverrides: {
      root: {
        padding: 0,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'start',
        gap: theme.unit(0.5),
      },
      avatar: {
        margin: 0,
      },
      content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.unit(0.5),
        flexGrow: 1,
        overflow: 'auto',
        ...theme.typography.M3.body.small,
      },
      title: {
        ...theme.typography.M3.label.large,
        color: theme.palette.M3.sys.light['on-surface'],
      },
      subheader: {
        ...theme.typography.M3.body.small,
        color: theme.palette.M3.sys.light['on-surface-variant'],
      },
      action: {
        flexShrink: 0,
        margin: 0,
        alignSelf: 'start',
      },
    },
  };

  // ─────────────────────────────────────────────────────────
  // MUI CARD CONTENT THEME
  // ─────────────────────────────────────────────────────────
  const cardContentTheme: Components<Theme>['MuiCardContent'] = {
    styleOverrides: {
      root: {
        padding: 0,
        ...theme.typography.M3.body.medium,
        color: theme.palette.M3.sys.light['on-surface-variant'],
        '&:last-child': {
          paddingBottom: 0,
        },
      },
    },
  };

  // ─────────────────────────────────────────────────────────
  // MUI CARD ACTIONS THEME
  // ─────────────────────────────────────────────────────────
  const cardActionsTheme: Components<Theme>['MuiCardActions'] = {
    styleOverrides: {
      root: {
        padding: 0,
        gap: theme.unit(2),
        borderTop: `1px solid ${theme.palette.M3.sys.light['outline 3']}`,
      },
    },
  };

  return {
    components: {
      MuiCard: cardTheme,
      MuiCardHeader: cardHeaderTheme,
      MuiCardContent: cardContentTheme,
      MuiCardActions: cardActionsTheme,
    },
  };
};

export default getCardTheme;
